import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, BookOpen, Calendar, User, Tag } from "lucide-react";
import { findPost, BLOG_POSTS } from "@/lib/blogPosts";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

function renderBlock(b, i) {
  const stableKey = `${b.type}-${i}-${(b.text || (b.items && b.items[0]) || "").slice(0, 32)}`;
  if (b.type === "h") return <h2 key={stableKey} className="mt-10 mb-3 font-display font-semibold text-[22px] md:text-[24px] tracking-tight text-slate-900">{b.text}</h2>;
  if (b.type === "list") return (
    <ul key={stableKey} className="mt-3 mb-5 space-y-2 pl-5 list-disc marker:text-cyan-600">
      {b.items.map((it) => <li key={it} className="text-[15px] leading-relaxed text-slate-700">{it}</li>)}
    </ul>
  );
  return <p key={stableKey} className="my-4 text-[15.5px] leading-[1.7] text-slate-700">{b.text}</p>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = findPost(slug);

  useEffect(() => {
    if (!post) return;
    const prevTitle = document.title;
    document.title = `${post.title} · RiskEye Blog`;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", post.excerpt);
    const link = document.querySelector('link[rel="canonical"]');
    const prevHref = link?.getAttribute("href");
    link?.setAttribute("href", `https://riskeye.io/blog/${post.slug}`);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
      if (link && prevHref != null) link.setAttribute("href", prevHref);
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const others = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "datePublished": post.published,
    "author": { "@type": "Person", "name": post.author },
    "publisher": { "@type": "Organization", "name": "RiskEye", "url": "https://riskeye.io/" },
    "keywords": (post.keywords || []).join(", "),
    "mainEntityOfPage": { "@type": "WebPage", "@id": `https://riskeye.io/blog/${post.slug}` },
  };

  return (
    <div className="min-h-screen bg-[#FBFBF9] text-slate-900 font-sans" data-testid="blog-post-page">
      {/* JSON-LD is safe: content is author-controlled from a static file, and </ is escaped to prevent script-tag breakout. */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }} />
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/70">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-3.5">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-rose-500 via-amber-500 to-emerald-500 p-[1.5px]">
              <div className="h-full w-full rounded-[5px] bg-[#050B1F] flex items-center justify-center">
                <div className="h-1.5 w-1.5 rounded-full bg-rose-400" />
              </div>
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[19px] tracking-tight leading-none">RiskEye</div>
              <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-slate-500 mt-0.5">by seQure</div>
            </div>
          </Link>
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-[13.5px] text-slate-600 hover:text-slate-900" data-testid="blog-post-back">
            <ArrowLeft className="h-3.5 w-3.5" /> All guides
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 pt-14 pb-16">
        <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1 text-[11.5px] font-mono uppercase tracking-[0.18em] text-slate-500 shadow-sm mb-6">
          <BookOpen className="h-3 w-3 text-cyan-600" /> {post.kicker}
        </div>
        <h1 className="font-display font-bold text-[36px] md:text-[46px] leading-[1.08] tracking-tight text-slate-900">{post.title}</h1>
        <p className="mt-5 text-[17px] text-slate-600 leading-relaxed">{post.excerpt}</p>
        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11.5px] font-mono uppercase tracking-[0.22em] text-slate-500 pb-6 border-b border-slate-200">
          <span className="flex items-center gap-1.5"><User className="h-3 w-3" /> {post.author}</span>
          <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {formatDate(post.published)}</span>
          <span className="flex items-center gap-1.5"><Clock className="h-3 w-3" /> {post.read_minutes} min read</span>
          <span className="flex items-center gap-1.5"><Tag className="h-3 w-3" /> {post.tag}</span>
        </div>
        <div className="mt-2" data-testid="blog-post-body">
          {post.body.map((b, i) => renderBlock(b, i))}
        </div>
        <div className="mt-14 rounded-2xl border border-slate-200 bg-white p-6 md:p-7">
          <div className="text-[10.5px] font-mono uppercase tracking-[0.28em] text-cyan-700 mb-2">Talk to us</div>
          <div className="font-display font-semibold text-[19px] tracking-tight text-slate-900">Want us to walk this through with your team?</div>
          <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">A 30-minute session with a seQure advisor is free. We'll seed a private tenant, walk through the working, and hand you super-admin keys the same day.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a href="https://calendly.com/sequrenz/30min" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#0A1329] hover:bg-[#162347] text-white font-medium text-[13.5px] px-4 py-2 rounded-md transition-colors" data-testid="blog-post-book">
              Book a demo <ArrowRight className="h-3.5 w-3.5" />
            </a>
            <a href="mailto:hello@riskeye.io" className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-800 font-medium text-[13.5px] px-4 py-2 rounded-md transition-colors">
              hello@riskeye.io
            </a>
          </div>
        </div>
      </article>

      {others.length > 0 && (
        <section className="max-w-4xl mx-auto px-6 pb-24">
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-mono text-slate-400 mb-4">Keep reading</div>
          <div className="grid md:grid-cols-2 gap-4">
            {others.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group rounded-xl border border-slate-200 bg-white p-5 hover:border-slate-900 hover:shadow-[0_12px_30px_-16px_rgba(15,23,42,0.35)] transition-all" data-testid={`blog-post-related-${p.slug}`}>
                <div className="text-[10.5px] font-mono uppercase tracking-[0.24em] text-cyan-700 font-semibold">{p.kicker}</div>
                <div className="mt-2 font-display font-semibold text-[16px] tracking-tight text-slate-900 group-hover:text-cyan-800 transition-colors leading-snug">{p.title}</div>
                <div className="mt-2 text-[12.5px] text-slate-600 leading-snug line-clamp-2">{p.excerpt}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[11.5px] font-mono uppercase tracking-[0.22em] text-slate-400">
          <span>© {new Date().getFullYear()} RiskEye · by seQure</span>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-slate-800">Home</Link>
            <Link to="/blog" className="hover:text-slate-800">Blog</Link>
            <Link to="/about" className="hover:text-slate-800">About</Link>
            <Link to="/trust" className="hover:text-slate-800">Trust</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
